import type { InquiryTypeCode, OtherInquiryTypeCode } from '@/pages/photo/studio_inquiries/types'
import type { FavoriteType } from '@/utils/favoriteStorage'

type DataLayerValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | DataLayerValue[]
  | { [key: string]: DataLayerValue }

type ClickFavoriteEventParams = {
  favorite_type: FavoriteType
  favorite_id: number
}

// 投稿ページへのボタンの置き場所。ページは区別しない（ページ別の集計は GA4 が自動で付ける page_location で行う）
export type ReportPostLocation = 'header' | 'bottom_nav' | 'campaign' | 'hamburger' | 'footer' | 'mypage'

type ClickReportPostEventParams = {
  location: ReportPostLocation
}

export type StudioInquiryCompleteEventParams = {
  studio_id: number
  inquiry_type?: InquiryTypeCode
  other_inquiry_type?: OtherInquiryTypeCode
  entry_reservation_type?: InquiryTypeCode
}

export type GtmEventMap = {
  click_shindan: undefined
  click_report_post: ClickReportPostEventParams
  click_review_post: undefined
  click_favorite: ClickFavoriteEventParams
  click_external_marry: undefined
  click_external_mwed: undefined
  click_external_selfie: undefined
  studio_inquiry_complete: StudioInquiryCompleteEventParams
}

export type GtmEventName = Exclude<keyof GtmEventMap, 'studio_inquiry_complete'>

type AllGtmEventName = keyof GtmEventMap

type GtmEventNameWithParams = {
  [EventName in AllGtmEventName]: GtmEventMap[EventName] extends undefined ? never : EventName
}[AllGtmEventName]

export type GtmEventNameWithoutParams = {
  [EventName in AllGtmEventName]: GtmEventMap[EventName] extends undefined ? EventName : never
}[AllGtmEventName]

type DataLayerEvent = {
  event: string
} & Record<string, DataLayerValue>

declare global {
  interface Window {
    dataLayer?: DataLayerEvent[]
  }
}

export function pushVirtualPageView(path: string): void {
  let url: URL

  try {
    url = new URL(path, window.location.origin)
  } catch {
    return
  }

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event: 'virtual_page_view',
    page_location: url.href,
    page_path: url.pathname,
    page_title: document.title,
  })
}

export function pushEvent<T extends GtmEventNameWithoutParams>(eventName: T): void
export function pushEvent<T extends GtmEventNameWithParams>(
  eventName: T,
  params: GtmEventMap[T]
): void
export function pushEvent(
  eventName: AllGtmEventName,
  params?: Record<string, DataLayerValue>
): void {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({ ...params, event: eventName })
}

export function resolveExternalGtmEvent(
  url: string
): Extract<GtmEventNameWithoutParams, 'click_external_marry' | 'click_external_mwed' | 'click_external_selfie'> | undefined {
  let hostname: string
  try {
    hostname = new URL(url).hostname
  } catch {
    return undefined
  }
  if (hostname === 'anymarry.mwed.jp') return 'click_external_marry'
  // anymarry.mwed.jp は .mwed.jp のサブドメインに該当するため、上の判定を先に行う。
  if (hostname === 'mwed.jp' || hostname.endsWith('.mwed.jp')) return 'click_external_mwed'
  if (hostname === 'studio-selfie.jp' || hostname.endsWith('.studio-selfie.jp')) return 'click_external_selfie'
  return undefined
}
